const assetId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const legacyPath = /^\/pages\/register-high-roller\/(hrp[0-9]{1,20})\/?$/i;
const registrationPath = /^\/pages\/register-high-roller\/?$/i;
const registrationPrefix = /^\/pages\/register-high-roller\//i;
const registrations = new WeakMap();

export function readPaddleScan(search, pathname = '') {
  const legacy = legacyPath.exec(pathname);
  if (legacy) return { kind: 'code', code: legacy[1] };
  if (registrationPrefix.test(pathname) && !registrationPath.test(pathname)) return { kind: 'unavailable' };
  if (pathname && !registrationPath.test(pathname)) return { kind: 'manual' };
  const params = new URLSearchParams(search);
  const code = params.get('asset');
  if (code && assetId.test(code)) return { kind: 'code', code: code.toLowerCase() };
  if (code !== null || params.get('scan') === 'unavailable') return { kind: 'unavailable' };
  return { kind: 'manual' };
}

function feedback(root, message) {
  const status = root.querySelector('[data-scan-feedback]') || root.querySelector('[data-scan-status]');
  if (status) {
    status.textContent = message;
    status.hidden = !message;
  }
}

function stopFlip(root, state) {
  if (state.flipTimer !== null) state.view.clearTimeout(state.flipTimer);
  state.flipTimer = null;
  (root.querySelector('[data-scan-scene]') || root).classList?.remove('is-flipping');
  if (state.flipButton) {
    state.flipButton.disabled = false;
    state.flipButton.setAttribute('aria-label', 'Replay paddle turn');
    state.flipButton.setAttribute('aria-busy', 'false');
  }
}

function updateMotion(root, state) {
  root.toggleAttribute?.('data-motion-paused', state.motionPaused);
  const button = root.querySelector('[data-scan-motion]');
  if (button) {
    button.textContent = state.motionPaused ? 'Play motion' : 'Pause motion';
    button.setAttribute('aria-pressed', String(state.motionPaused));
    button.setAttribute('aria-label', state.motionPaused ? 'Play motion' : 'Pause motion');
  }
  if (state.motionPaused) stopFlip(root, state);
}

function bindControls(root, state) {
  root.addEventListener?.('input', event => {
    if (event.target === state.input) state.userEdited = true;
  });
  root.addEventListener?.('click', async event => {
    const button = event.target.closest?.('[data-scan-copy], [data-scan-flip], [data-scan-motion]');
    if (!button || !root.contains(button) || button.disabled) return;
    event.preventDefault();
    if (button.hasAttribute('data-scan-motion')) {
      state.motionPaused = !state.motionPaused;
      updateMotion(root, state);
      feedback(root, state.motionPaused ? 'Motion paused.' : 'Motion enabled.');
    } else if (button.hasAttribute('data-scan-flip')) {
      if (state.motionPaused) {
        feedback(root, 'Motion is paused. Your paddle code and registration details are unchanged.');
        return;
      }
      stopFlip(root, state);
      state.flipButton = button;
      button.disabled = true;
      button.setAttribute('aria-label', 'Paddle turn playing');
      button.setAttribute('aria-busy', 'true');
      (root.querySelector('[data-scan-scene]') || root).classList?.add('is-flipping');
      feedback(root, 'A visual paddle turn. This does not read or verify another chip.');
      state.flipTimer = state.view.setTimeout(() => stopFlip(root, state), 1000);
    } else {
      const location = state.view.location;
      if (!location?.origin || !location.pathname) {
        feedback(root, 'The scan link is unavailable. You can still send your purchase details below.');
        return;
      }
      // Deliberately omit all query parameters, fragments and form values.
      const details = location.origin + location.pathname + (state.scan.kind === 'code' ? '\nPaddle code: ' + state.scan.code : '');
      button.disabled = true;
      try {
        const clipboard = state.view.navigator?.clipboard;
        if (!clipboard?.writeText) throw new Error('clipboard_unavailable');
        await clipboard.writeText(details);
        feedback(root, 'Scan details copied.');
      } catch {
        feedback(root, 'Copy is unavailable here. Select and copy these scan details: ' + details);
      } finally {
        button.disabled = false;
      }
    }
  });
}

export function initializeRegistration(root, search, pathname = '') {
  let state = registrations.get(root);
  if (!state) {
    const view = root.ownerDocument?.defaultView || (typeof window === 'undefined' ? globalThis : window);
    state = { view, input: null, autoValue: undefined, userEdited: false, scan: null, pathname: '', flipTimer: null, flipButton: null, motionPaused: !!view.matchMedia?.('(prefers-reduced-motion: reduce)').matches };
    registrations.set(root, state);
    bindControls(root, state);
  }
  const input = root.querySelector('[data-paddle-code]');
  const status = root.querySelector('[data-scan-status]');
  const scan = readPaddleScan(search, pathname);
  if (state.scan && (state.pathname !== pathname || state.scan.kind !== scan.kind || state.scan.code !== scan.code)) {
    stopFlip(root, state);
    feedback(root, '');
  }
  state.scan = scan;
  state.pathname = pathname;
  if (input !== state.input) {
    state.input = input;
    state.autoValue = undefined;
    state.userEdited = !!input?.value;
  }
  const codeLabel = root.querySelector('[data-scan-code]');
  const scanLabel = root.querySelector('[data-scan-label]');
  if (codeLabel) codeLabel.textContent = scan.kind === 'code' ? scan.code : 'No code yet';
  if (scanLabel) scanLabel.textContent = scan.kind === 'code' ? 'LINK READ' : scan.kind === 'unavailable' ? 'LINK NEEDS REVIEW' : 'MANUAL REGISTRATION';
  if (scan.kind === 'code') {
    if (input && !state.userEdited && (!input.value || input.value === state.autoValue)) {
      input.value = scan.code;
      state.autoValue = scan.code;
    }
    if (status) {
      status.textContent = 'Paddle code read from your link. Confirm your purchase details below.';
      status.hidden = false;
    }
  } else {
    if (input && !state.userEdited && input.value === state.autoValue) input.value = '';
    state.autoValue = undefined;
    if (status) {
      status.textContent = scan.kind === 'unavailable' ? 'We couldn’t read a paddle code from this link. You can still send your purchase details below.' : '';
      status.hidden = scan.kind !== 'unavailable';
    }
  }
  updateMotion(root, state);
}

if (typeof document !== 'undefined') {
  const initialize = () => document.querySelectorAll('[data-paddle-register]').forEach(root => initializeRegistration(root, window.location.search, window.location.pathname));
  initialize();
  document.addEventListener('shopify:section:load', initialize);
  window.addEventListener('popstate', initialize);
  window.addEventListener('pageshow', initialize);
}
